/**
 * DONATION DASHBOARD - ADMIN VIEW
 * 
 * Admin dashboard for managing donations with statistics and list.
 * 
 * @module Donations/Components/DonationDashboard
 */

import React, { useState } from 'react';
import {
    HandCoins,
    Users,
    TrendingUp,
    Clock,
    CheckCircle,
    AlertCircle,
    XCircle,
    Eye,
    Trash2,
    Filter,
    Search,
    RefreshCw,
    Calendar,
    User,
    Mail,
    Loader2
} from 'lucide-react';
import { useDonations } from '../hooks/useDonations';
import { useDonationStats } from '../hooks/useDonationStats';
import { formatDate, formatAmount, getCategoryLabel, getMethodLabel } from '../utils/donationHelpers';
import { DonationStatus, Donation } from '../types/donation.types';

export const DonationDashboard: React.FC = () => {
    const { donations, loading, error, loadDonations, deleteDonation } = useDonations();
    const stats = useDonationStats(donations);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');

    const filteredDonations = donations.filter((d: Donation) => {
        const matchesSearch = d.donorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             d.donorEmail.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this donation?')) {
            try {
                await deleteDonation(id);
            } catch (error) {
                console.error('Failed to delete donation:', error);
            }
        }
    };

    const getStatusIcon = (status: DonationStatus) => {
        switch (status) {
            case DonationStatus.COMPLETED: return <CheckCircle size={14} color="#10b981" />;
            case DonationStatus.PENDING: return <Clock size={14} color="#f59e0b" />;
            case DonationStatus.FAILED: return <AlertCircle size={14} color="#ef4444" />;
            case DonationStatus.REFUNDED: return <XCircle size={14} color="#6b7280" />;
            default: return null;
        }
    };

    const handleRefresh = async () => {
        await loadDonations();
    };

    if (loading) {
        return (
            <div className="loading-state">
                <Loader2 size={32} className="spinner" />
                <p>Loading donations...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-state">
                <p>Error loading donations: {error}</p>
                <button className="btn-primary" onClick={handleRefresh}>
                    <RefreshCw size={16} />
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="donation-dashboard">
            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: '#EFF6FF' }}>
                        <HandCoins size={20} color="#3B82F6" />
                    </div>
                    <div>
                        <div className="stat-label">Total Donations</div>
                        <div className="stat-value">{stats.totalDonations}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: '#ECFDF5' }}>
                        <TrendingUp size={20} color="#10B981" />
                    </div>
                    <div>
                        <div className="stat-label">Total Amount</div>
                        <div className="stat-value">K {stats.totalAmount.toLocaleString()}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: '#F5F3FF' }}>
                        <Users size={20} color="#8B5CF6" />
                    </div>
                    <div>
                        <div className="stat-label">Donors</div>
                        <div className="stat-value">{donations.length}</div>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ backgroundColor: '#FFFBEB' }}>
                        <CheckCircle size={20} color="#F59E0B" />
                    </div>
                    <div>
                        <div className="stat-label">Avg. Donation</div>
                        <div className="stat-value">K {stats.averageAmount.toFixed(2)}</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="admin-filters">
                <div className="filter-row">
                    <div className="search-wrapper">
                        <Search size={16} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search donors..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="filter-controls">
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                            <option value="refunded">Refunded</option>
                        </select>
                        <button className="filter-toggle" onClick={handleRefresh}>
                            <RefreshCw size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Donations Table */}
            <div className="donations-table-wrapper">
                <table className="donations-table">
                    <thead>
                        <tr>
                            <th>Donor</th>
                            <th>Amount</th>
                            <th>Category</th>
                            <th>Method</th>
                            <th>Date</th>
                            <th>Status</th>
                            <th className="actions-column">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDonations.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="empty-state">
                                    <p>No donations found.</p>
                                </td>
                            </tr>
                        ) : (
                            filteredDonations.map((donation: Donation) => (
                                <tr key={donation._id}>
                                    <td>
                                        <div className="donor-cell">
                                            <div className="donor-avatar">
                                                {donation.isAnonymous ? (
                                                    <User size={14} />
                                                ) : (
                                                    donation.donorName.charAt(0).toUpperCase()
                                                )}
                                            </div>
                                            <div>
                                                <div className="donor-name">
                                                    {donation.isAnonymous ? 'Anonymous' : donation.donorName}
                                                </div>
                                                <div className="donor-email">
                                                    <Mail size={12} />
                                                    {donation.donorEmail}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="amount-cell">
                                        {formatAmount(donation.amount, donation.currency)}
                                    </td>
                                    <td>
                                        <span className="category-tag">
                                            {getCategoryLabel(donation.category)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="method-tag">
                                            {getMethodLabel(donation.method)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="date-cell">
                                            <Calendar size={14} />
                                            {formatDate(donation.createdAt)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${donation.status}`}>
                                            {getStatusIcon(donation.status)}
                                            {donation.status}
                                        </span>
                                    </td>
                                    <td className="actions-cell">
                                        <button
                                            className="action-btn"
                                            aria-label="View donation"
                                        >
                                            <Eye size={14} />
                                        </button>
                                        <button
                                            className="action-btn"
                                            onClick={() => handleDelete(donation._id!)}
                                            aria-label="Delete donation"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DonationDashboard;
